import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";

import { logger } from "@/lib/logger";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error("ErrorBoundary caught an error", { error, componentStack: info.componentStack });
    this.props.onError?.(error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;

    if (!error) {
      return this.props.children;
    }

    const { fallback } = this.props;

    if (typeof fallback === "function") {
      return fallback(error, this.reset);
    }

    if (fallback) {
      return fallback;
    }

    return (
      <div
        className="mx-auto max-w-2xl space-y-4 px-6 py-16 text-center"
        role="alert"
      >
        <p className="eyebrow">Unexpected error</p>
        <h1 className="text-3xl font-semibold text-foreground">
          Something went wrong while rendering this view.
        </h1>
        <p className="text-sm leading-6 text-muted-foreground">
          The seed caught the error at the top-level boundary. Reload the page or restart the
          development server to recover.
        </p>
        <button
          className="inline-flex min-h-10 items-center justify-center rounded-full border border-border px-4 py-2 text-sm font-medium transition hover:border-primary hover:text-primary"
          onClick={this.reset}
          type="button"
        >
          Try again
        </button>
      </div>
    );
  }
}
